import dayjs from "dayjs";
import {
  CandidateSearchOption,
  CandidateSortKey,
  CandidateUser,
  CountRequest,
  CountForAgentRequest,
  CandidateSortOrder,
  SearchRequest,
  SearchForAgentRequest
} from "@/pb/scout/candidate/v1/candidate_pb";
import { DisplayMode, SortOrderType } from "./ScoutCandidateHeader/types";
import { Candidate } from "./ScoutCandidateCardList/ScoutCandidateCard/types";
import {
  SearchOptionParam,
  defaultSearchOptionParam
} from "@/components/Pages/Scout/Candidates/SearchOption/types";
import {
  getCandidateCount,
  getAgentCandidateCount,
  getCandidates,
  getAgentCandidates
} from "@/grpc-web/scout/candidate";
import { activitySelectGroupItems } from "@/components/Pages/Scout/Candidates/SearchOption/consts";
import { SHA256 } from "crypto-js";
import {
  AgentScoutSearchStorageKey,
  ScoutSearchStorageKey
} from "@/components/Pages/Scout/Candidates/consts";
import DefaultUserImage from "@/assets/images/Pages/Scout/Candidates/DefaultUserImage.svg";
import { useStore } from "@/store";
import {
  bulkSendScoutMessage,
  bulkSendScoutMessageForAgent,
  reserveScoutMessage,
  reserveScoutMessageForAgent
} from "@/grpc-web/scout/message";
import { DisplayModeStorageKey } from "@/components/Pages/Scout/Candidates/types";

// スカウトエージェントメニューがアクティブ状態かどうかを判定する
const isScoutAgentMenuActive = () => {
  const store = useStore();
  return store.getters["domains/user/isScoutAgentMenuActive"];
};

export const sortOrderTypeToCandidateSortKey = (value: SortOrderType) => {
  switch (value) {
    case SortOrderType.LAST_ACCESS_AT_DESC:
      return CandidateSortKey.CANDIDATE_SORT_KEY_LAST_ACCESS_AT;
    case SortOrderType.REGISTER_AT_DESC:
      return CandidateSortKey.CANDIDATE_SORT_KEY_MEMBER_REGISTRATION_AT;
    case SortOrderType.UPDATED_AT_DESC:
      return CandidateSortKey.CANDIDATE_SORT_KEY_MEMBER_INFO_UPDATED_AT;
    case SortOrderType.ACTIVITY_COUNT_DESC:
      return CandidateSortKey.CANDIDATE_SORT_KEY_ACTIVITIES_COUNT;
    default:
      return CandidateSortKey.CANDIDATE_SORT_KEY_UNSPECIFIED;
  }
};

// CandidateUserからカードに表示するHeader文字列を取得する
export const getHeader = (candidate: CandidateUser) => {
  return [
    candidate.getUniversityName(),
    candidate.getDepartment(),
    candidate.getConcentration()
  ].join(" ");
};

// CandidateUserからカードに表示するDescription文字列を取得する
export const getDescription = (candidate: CandidateUser) => {
  return [
    candidate.getCourse(),
    candidate.getGrade(),
    candidate.getGraduateYear(),
    "|",
    candidate.getUniqueId(),
    "|",
    candidate.hasLastAccessAtMs()
      ? getLastAccessLabel(candidate.getLastAccessAtMs())
      : "アクセスなし"
  ].join(" ");
};

export const getLastAccessLabel = (timestamp: number) => {
  const timestampdayjs = dayjs(timestamp);
  if (timestampdayjs.isAfter(dayjs().subtract(1, "minute"))) {
    return "1分以内にアクセス";
  }
  if (timestampdayjs.isAfter(dayjs().subtract(5, "minutes"))) {
    return "5分以内にアクセス";
  }
  if (timestampdayjs.isAfter(dayjs().subtract(10, "minutes"))) {
    return "10分以内にアクセス";
  }
  if (timestampdayjs.isAfter(dayjs().subtract(30, "minutes"))) {
    return "30分以内にアクセス";
  }
  if (timestampdayjs.isAfter(dayjs().subtract(1, "hour"))) {
    return "1時間以内にアクセス";
  }
  if (timestampdayjs.isAfter(dayjs().subtract(1, "day"))) {
    return "1日以内にアクセス";
  }
  if (timestampdayjs.isAfter(dayjs().subtract(1, "week"))) {
    return "1週間以内にアクセス";
  }
  if (timestampdayjs.isAfter(dayjs().subtract(1, "month"))) {
    return "1ヶ月以内にアクセス";
  }

  return "1ヶ月以上前にアクセス";
};

// protoファイルから生成されたクラスを画面表示で扱うinterfaceに変換
export const toCandidate = (item: CandidateUser, userImageUrl?: string) => {
  const studentEndeavorContent = item.hasStudentEndeavor()
    ? item.getStudentEndeavor()
    : undefined;
  const promotionContent = item.hasSelfPromotion()
    ? item.getSelfPromotion()
    : undefined;

  return {
    userId: item.getUserId(),
    userImageUrl: userImageUrl || DefaultUserImage,
    uniqueId: item.getUniqueId(),
    header: getHeader(item),
    description: getDescription(item),
    starred: item.getVoted(),
    aspiringBusinessCategories: item.getAspiringBusinessCategoriesList(),
    preferredJobTypes: item.getPreferredJobTypesList(),
    internshipResultCompanies: item.getInternshipResultCompanyNamesList(),
    studentEndeavorContent: studentEndeavorContent,
    preferredWorkPrefectures: item.getPreferredWorkPrefecturesList(),
    graduateYear: item.getGraduateYear(),
    selfPromotionContent: promotionContent,
    seminarCircle: item.getSeminarCircle()
  } as Candidate;
};

export const autoCloseToast = (closeToastFunc: () => void) => {
  setTimeout(() => {
    closeToastFunc();
    // 以下の実装を参考に自動でトーストが閉じる時間を設定した。
    // c.f. https://github.com/OneCareerJP/cloud/blob/3bb5d4cb94ec61ad7a36e6fca7df032a44086d49/frontend/src/components/Pages/Entries/index.vue#L859
  }, 10000);
};

// SearchOptionParam から初期値を考慮しつつ、 CandidateSearchOption に変換する
export const convertToCandidateSearchOption = (param: SearchOptionParam) => {
  const searchOption = new CandidateSearchOption();
  if (param.keyword !== "") searchOption.setKeywordForLikeSearch(param.keyword);
  if (param.graduateYears.length > 0)
    searchOption.setGraduateYearsList(param.graduateYears);
  if (param.aspiringBusinessCategoryIds.length > 0)
    searchOption.setAspiringBusinessCategoryIdsList(
      param.aspiringBusinessCategoryIds
    );
  if (param.preferredJobTypeIds.length > 0)
    searchOption.setPreferredJobTypeIdsList(param.preferredJobTypeIds);
  if (param.universityAreaIds.length > 0)
    searchOption.setUniversityAreaIdsList(param.universityAreaIds);
  if (param.universityNames.length > 0)
    searchOption.setUniversityNamesList(param.universityNames);
  if (param.grades.length > 0) searchOption.setGradesList(param.grades);
  if (param.isUsingInformationForWomen !== undefined)
    searchOption.setSubscribeToInfoForWomen(param.isUsingInformationForWomen);
  if (param.courses.length > 0) searchOption.setCoursesList(param.courses);
  if (param.departmentNames.length > 0)
    searchOption.setDepartmentNamesList(param.departmentNames);
  if (param.concentrationNames.length > 0)
    searchOption.setConcentrationNamesList(param.concentrationNames);
  if (param.highSchoolName !== "")
    searchOption.setHighSchoolName(param.highSchoolName);
  if (param.internshipResultCompanyNames.length > 0)
    searchOption.setInternshipResultCompanyNamesList(
      param.internshipResultCompanyNames
    );
  if (param.activityCategories.length > 0) {
    searchOption.setActivityLargeCategoryNamesList(
      param.activityCategories.map(category => category.largeCategoryName)
    );
    searchOption.setActivityMediumCategoryNamesList(
      param.activityCategories.flatMap(category => category.mediumCategoryNames)
    );
  }
  if (param.programmingSkillNames.length > 0)
    searchOption.setProgrammingSkillNamesList(param.programmingSkillNames);
  if (param.programmingSkillYearOfUses.length > 0)
    searchOption.setProgrammingSkillYearOfUsesList(
      param.programmingSkillYearOfUses
    );
  if (param.uniqueId !== "") searchOption.setUniqueId(param.uniqueId);
  searchOption.setLastAccessRangeKey(param.lastAccessRangeKey);
  searchOption.setVotedUserOnly(param.votedUserOnly);
  searchOption.setVoterUserOnly(param.voterUserOnly);
  searchOption.setSelfPromotionExists(param.selfPromotionExists);
  searchOption.setStudentEndeavorExists(param.studentEndeavorExists);
  searchOption.setResearchContentExists(param.researchContentExists);
  searchOption.setLineIdExists(param.lineIdExists);
  if (param.preferredWorkPrefectureIds.length > 0)
    searchOption.setPreferredWorkPrefectureIdsList(
      param.preferredWorkPrefectureIds
    );
  if (param.companyChoiceFactorTypeIds.length > 0)
    searchOption.setCompanyChoiceFactorTypeIdsList(
      param.companyChoiceFactorTypeIds
    );
  searchOption.setInternshipResultExists(param.internshipResultExists);
  if (param.languageAbilityNames.length > 0)
    searchOption.setLanguageAbilityNamesList(param.languageAbilityNames);
  if (param.languageAbilityLevels.length > 0)
    searchOption.setLanguageAbilityLevelsList(param.languageAbilityLevels);

  return searchOption;
};

export const setSearchOptionParam = (
  searchOptionParam: SearchOptionParam,
  searchOption: CandidateSearchOption
) => {
  searchOptionParam.keyword = (() => {
    if (searchOption.hasKeywordForLikeSearch()) {
      return searchOption.getKeywordForLikeSearch();
    }
    // 検索パラメータにキーワードがない場合、空文字として扱う
    return "";
  })();
  searchOptionParam.graduateYears = searchOption.getGraduateYearsList();
  searchOptionParam.aspiringBusinessCategoryIds = searchOption.getAspiringBusinessCategoryIdsList();
  searchOptionParam.preferredJobTypeIds = searchOption.getPreferredJobTypeIdsList();
  searchOptionParam.universityAreaIds = searchOption.getUniversityAreaIdsList();
  searchOptionParam.universityNames = searchOption.getUniversityNamesList();
  searchOptionParam.grades = searchOption.getGradesList();
  searchOptionParam.isUsingInformationForWomen = (() => {
    if (searchOption.hasSubscribeToInfoForWomen()) {
      return searchOption.getSubscribeToInfoForWomen();
    }
    return undefined;
  })();
  searchOptionParam.courses = searchOption.getCoursesList();
  searchOptionParam.departmentNames = searchOption.getDepartmentNamesList();
  searchOptionParam.concentrationNames = searchOption.getConcentrationNamesList();
  searchOptionParam.highSchoolName = (() => {
    if (searchOption.hasHighSchoolName()) {
      return searchOption.getHighSchoolName();
    }
    // 検索パラメータに高校名がない場合、空文字として扱う
    return "";
  })();
  searchOptionParam.internshipResultCompanyNames = searchOption.getInternshipResultCompanyNamesList();
  searchOptionParam.activityCategories = convertSearchOptionActivityToSearchOptionParamActivity(
    searchOption.getActivityLargeCategoryNamesList(),
    searchOption.getActivityMediumCategoryNamesList()
  );
  searchOptionParam.programmingSkillNames = searchOption.getProgrammingSkillNamesList();
  searchOptionParam.programmingSkillYearOfUses = searchOption.getProgrammingSkillYearOfUsesList();
  searchOptionParam.lastAccessRangeKey = searchOption.getLastAccessRangeKey();
  searchOptionParam.votedUserOnly = searchOption.getVotedUserOnly();
  searchOptionParam.voterUserOnly = searchOption.getVoterUserOnly();
  searchOptionParam.uniqueId = (() => {
    if (searchOption.hasUniqueId()) {
      return searchOption.getUniqueId();
    }
    // 検索パラメータに識別番号がない場合、空文字として扱う
    return "";
  })();
  searchOptionParam.selfPromotionExists = searchOption.getSelfPromotionExists();
  searchOptionParam.studentEndeavorExists = searchOption.getStudentEndeavorExists();
  searchOptionParam.researchContentExists = searchOption.getResearchContentExists();
  searchOptionParam.lineIdExists = searchOption.getLineIdExists();
  searchOptionParam.preferredWorkPrefectureIds = searchOption.getPreferredWorkPrefectureIdsList();
  searchOptionParam.companyChoiceFactorTypeIds = searchOption.getCompanyChoiceFactorTypeIdsList();
  searchOptionParam.internshipResultExists = searchOption.getInternshipResultExists();
  searchOptionParam.languageAbilityNames = searchOption.getLanguageAbilityNamesList();
  searchOptionParam.languageAbilityLevels = searchOption.getLanguageAbilityLevelsList();
};

// スカウト候補者の総件数を取得する
export const fetchCandidateCount = async (
  searchOption: CandidateSearchOption
) => {
  if (isScoutAgentMenuActive()) {
    // エージェントメニューがアクティブな場合エージェント向けのAPIを呼び出して結果を返却する
    const countForAgentRequest = new CountForAgentRequest();
    countForAgentRequest.setSearchOption(searchOption);

    return await getAgentCandidateCount(countForAgentRequest);
  }

  const countRequest = new CountRequest();
  countRequest.setSearchOption(searchOption);

  return await getCandidateCount(countRequest);
};

export const fetchCandidates = async (
  searchOption: CandidateSearchOption,
  sortKey: CandidateSortKey,
  sortOrder: CandidateSortOrder,
  page: number,
  per: number
) => {
  if (isScoutAgentMenuActive()) {
    // エージェントメニューがアクティブな場合エージェント向けのAPIを呼び出して結果を返却する
    const searchForAgentRequest = new SearchForAgentRequest();
    searchForAgentRequest.setSortKey(sortKey);
    searchForAgentRequest.setSortOrder(sortOrder);
    searchForAgentRequest.setPer(per);
    searchForAgentRequest.setPage(page);
    searchForAgentRequest.setSearchOption(searchOption);

    return await getAgentCandidates(searchForAgentRequest);
  }

  const searchRequest = new SearchRequest();
  searchRequest.setSortKey(sortKey);
  searchRequest.setSortOrder(sortOrder);
  searchRequest.setPer(per);
  searchRequest.setPage(page);
  searchRequest.setSearchOption(searchOption);

  return await getCandidates(searchRequest);
};

export const reserveScoutMessageAction = async (
  toUserId: number,
  text: string,
  scheduledAtMs: number,
  messageTemplateId: number | undefined
) => {
  if (isScoutAgentMenuActive()) {
    await reserveScoutMessageForAgent(
      toUserId,
      text,
      scheduledAtMs,
      messageTemplateId
    );
    return;
  }

  await reserveScoutMessage(toUserId, text, scheduledAtMs, messageTemplateId);
};

export const bulkSendScoutMessageAction = async (
  toUserIds: number[],
  text: string,
  messageTemplateId: number | undefined
) => {
  if (isScoutAgentMenuActive()) {
    return await bulkSendScoutMessageForAgent(
      toUserIds,
      text,
      messageTemplateId
    );
  }

  return await bulkSendScoutMessage(toUserIds, text, messageTemplateId);
};

export const convertSearchOptionActivityToSearchOptionParamActivity = (
  activityLargeCategoryNames: string[],
  activityMediumCategoryNames: string[]
) => {
  const filteredActivitySelectGroupItems = activitySelectGroupItems.filter(
    activitySelectGroupItem => {
      return activityLargeCategoryNames.includes(
        String(activitySelectGroupItem.item.value)
      );
    }
  );
  return filteredActivitySelectGroupItems.map(activitySelectGroupItem => {
    const subItems = activitySelectGroupItem.subItems
      .filter(activitySelectGroupItemSubItem => {
        return activityMediumCategoryNames.includes(
          String(activitySelectGroupItemSubItem.value)
        );
      })
      .map(activitySelectGroupItemSubItem => {
        return String(activitySelectGroupItemSubItem.value);
      });
    return {
      largeCategoryName: String(activitySelectGroupItem.item.value),
      mediumCategoryNames: subItems
    };
  });
};

export const generateSearchOptionParamHash = (param: SearchOptionParam) => {
  return SHA256(JSON.stringify(param)).toString();
};

export const getSearchOptionParamStorageKey = () => {
  if (isScoutAgentMenuActive()) {
    return AgentScoutSearchStorageKey.SEARCH_OPTION_PARAM;
  } else {
    return ScoutSearchStorageKey.SEARCH_OPTION_PARAM;
  }
};

export const getSavedSearchOptionStorageKey = () => {
  if (isScoutAgentMenuActive()) {
    return AgentScoutSearchStorageKey.SAVED_SEARCH_OPTION;
  } else {
    return ScoutSearchStorageKey.SAVED_SEARCH_OPTION;
  }
};

export const clearSearchOptionInLocalStorage = () => {
  localStorage.removeItem(getSearchOptionParamStorageKey());
  localStorage.removeItem(getSavedSearchOptionStorageKey());
};

export const getSavedSearchOptionFromLocalStorage = () => {
  const param = localStorage.getItem(getSavedSearchOptionStorageKey());
  return param ? JSON.parse(param) : null;
};

export const getSearchOptionParamFromLocalStorage = () => {
  const param = localStorage.getItem(getSearchOptionParamStorageKey());
  if (param == null) {
    return null;
  }
  const storedSearchOptionParam = param ? JSON.parse(param) : null;

  // 検索パラメータを新規で追加した場合、追加前に保存されたローカルストレージの検索条件にそのパラメータが存在しないと
  // エラーが発生するため、復元のタイミングでdefault値とマージする処理
  const searchOptionParam = Object.assign(
    {},
    defaultSearchOptionParam,
    storedSearchOptionParam
  );

  return searchOptionParam;
};

export const storeParamToLocalStorage = (key: string, param: any) => {
  localStorage.setItem(
    key,
    JSON.stringify(param, (key, value) => {
      return value === undefined ? null : value;
    })
  );
};

export const scrollTopByClassName = (className: string) => {
  const scrollElement = document.getElementsByClassName(className)[0];
  if (scrollElement) {
    scrollElement.scrollIntoView();
  }
};

const getDisplayModeStorageKey = () => {
  if (isScoutAgentMenuActive()) {
    return DisplayModeStorageKey.Agent;
  }

  return DisplayModeStorageKey.Standard;
};

export const getDisplayModeFromLocalStorage = () => {
  const displayMode = localStorage.getItem(getDisplayModeStorageKey());
  return displayMode ? (displayMode as DisplayMode) : DisplayMode.CARD;
};

export const storeDisplayModeToLocalStorage = (displayMode: DisplayMode) => {
  localStorage.setItem(getDisplayModeStorageKey(), displayMode);
};
